import React from 'react';
import { FaSearch, FaPlus, FaTimes, FaFileInvoiceDollar } from 'react-icons/fa';
import { getStatusBadge } from './statusBadges';

const InvoiceInfoPanel = ({
  invoice,
  showSupplierSearch,
  setShowSupplierSearch,
  supplierSearchQuery,
  supplierSearchResults,
  supplierSearchLoading,
  handleSupplierSearch,
  selectSupplier,
  supplierSearchRef,
  supplierDropdownRef,
  setShowAddSupplierModal
}) => {
  const totalItems = invoice?.items?.length || 0;
  const mappedItems = invoice?.items?.filter(p => p.produkt_id || p.mapped_product_id).length || 0;
  const unmappedItems = totalItems - mappedItems;
  const mappingPercent = totalItems > 0 ? Math.round((mappedItems / totalItems) * 100) : 0;

  const isCompleted = ['zatwierdzona', 'dodana', 'zrealizowana'].includes(invoice?.status);

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '2fr 1fr',
      gap: '1rem',
      marginBottom: '1rem'
    }}>
      {/* Left column: invoice data */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '2px solid var(--info)',
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          <FaFileInvoiceDollar style={{ color: 'var(--info)', fontSize: '1rem' }} />
          <span style={{ fontWeight: '700', color: 'var(--text)', fontSize: '0.95rem' }}>
            Dane faktury
          </span>
        </div>

        <div style={{ padding: '1rem 1.25rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

            {/* Left sub-column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <DataField label="Numer faktury" value={invoice?.numer_faktury || `FZ-${invoice?.id}`} large />
              <DataField
                label="Data faktury"
                value={invoice?.data_faktury ? new Date(invoice.data_faktury).toLocaleDateString('pl-PL') : '-'}
              />
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                  Dostawca
                </div>
                <SupplierCell
                  invoice={invoice}
                  showSupplierSearch={showSupplierSearch}
                  setShowSupplierSearch={setShowSupplierSearch}
                  supplierSearchQuery={supplierSearchQuery}
                  supplierSearchResults={supplierSearchResults}
                  supplierSearchLoading={supplierSearchLoading}
                  handleSupplierSearch={handleSupplierSearch}
                  selectSupplier={selectSupplier}
                  supplierSearchRef={supplierSearchRef}
                  supplierDropdownRef={supplierDropdownRef}
                  setShowAddSupplierModal={setShowAddSupplierModal}
                />
              </div>
              {(invoice?.dostawca_nazwa || invoice?.dostawca_adres || invoice?.dostawca_nip) && (
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                    Dane kontrahenta
                  </div>
                  <div style={{ color: 'var(--text)', fontSize: '0.875rem', lineHeight: '1.5' }}>
                    {invoice?.dostawca_nazwa && (
                      <div style={{ fontWeight: '600' }}>{invoice.dostawca_nazwa}</div>
                    )}
                    {invoice?.dostawca_adres && (
                      <div style={{ color: 'var(--text-secondary)' }}>{invoice.dostawca_adres}</div>
                    )}
                    {invoice?.dostawca_nip && (
                      <div style={{ color: 'var(--text-secondary)' }}>NIP: {invoice.dostawca_nip}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right sub-column - amounts */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <AmountField label="Suma netto" value={invoice?.suma_netto} color="var(--info)" />
              <AmountField label="Suma VAT" value={invoice?.suma_vat} color="var(--danger)" />
              <div style={{
                padding: '0.75rem',
                backgroundColor: 'var(--success-light)',
                borderRadius: '6px',
                borderLeft: '3px solid var(--success)'
              }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.25rem' }}>
                  Suma brutto
                </div>
                <div style={{ fontSize: '1.25rem', fontWeight: '800', color: 'var(--success)' }}>
                  {invoice?.suma_brutto ? `${parseFloat(invoice.suma_brutto).toFixed(2)} zl` : '-'}
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.35rem' }}>
                  Status
                </div>
                {getStatusBadge(invoice?.status)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right column: statistics */}
      <div style={{
        backgroundColor: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '0.75rem 1rem',
          borderBottom: '2px solid var(--warning)'
        }}>
          <span style={{ fontWeight: '700', color: 'var(--text)', fontSize: '0.95rem' }}>
            Mapowanie
          </span>
        </div>
        <div style={{ padding: '1rem 1.25rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
            <StatRow label="Pozycje na fakturze" value={totalItems} color="var(--info)" />
            <StatRow label="Zmapowane" value={mappedItems} color="var(--success)" />
            <StatRow label="Niezmapowane" value={unmappedItems} color="var(--danger)" />
          </div>

          {totalItems > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: '0.4rem'
              }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: '500' }}>
                  Postep
                </span>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: 'var(--text)' }}>
                  {mappingPercent}%
                </span>
              </div>
              <div style={{
                width: '100%', height: '10px', backgroundColor: 'var(--border)',
                borderRadius: '5px', overflow: 'hidden'
              }}>
                <div style={{
                  width: `${mappingPercent}%`, height: '100%',
                  backgroundColor: mappingPercent === 100 ? 'var(--success)' : 'var(--warning)',
                  borderRadius: '5px',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
          )}

          <div style={{
            padding: '0.75rem',
            backgroundColor: isCompleted ? 'var(--success-light)' : 'var(--warning-light)',
            borderRadius: '6px',
            textAlign: 'center',
            borderLeft: `3px solid ${isCompleted ? 'var(--success)' : 'var(--warning)'}`
          }}>
            <div style={{
              fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px',
              color: isCompleted ? 'var(--success)' : 'var(--warning)',
              marginBottom: '0.35rem'
            }}>
              Status faktury
            </div>
            {getStatusBadge(invoice?.status)}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---- Helper components ---- */

const DataField = ({ label, value, large }) => (
  <div>
    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>
      {label}
    </div>
    <div style={{ fontSize: large ? '1.05rem' : '0.9rem', fontWeight: large ? '700' : '500', color: 'var(--text)' }}>
      {value}
    </div>
  </div>
);

const AmountField = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>
      {label}
    </div>
    <div style={{ fontSize: '1rem', fontWeight: '700', color }}>
      {value ? `${parseFloat(value).toFixed(2)} zl` : '-'}
    </div>
  </div>
);

const StatRow = ({ label, value, color }) => (
  <div style={{
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '0.35rem 0',
    borderBottom: '1px solid var(--border)'
  }}>
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: '500' }}>{label}</span>
    <span style={{ fontWeight: '700', color, fontSize: '1.1rem' }}>{value}</span>
  </div>
);

const SupplierCell = ({
  invoice,
  showSupplierSearch,
  setShowSupplierSearch,
  supplierSearchQuery,
  supplierSearchResults,
  supplierSearchLoading,
  handleSupplierSearch,
  selectSupplier,
  supplierSearchRef,
  supplierDropdownRef,
  setShowAddSupplierModal
}) => {
  if (!showSupplierSearch) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{
          color: invoice?.dostawca_nazwa ? 'var(--text)' : 'var(--danger)',
          fontWeight: invoice?.dostawca_nazwa ? '500' : '600',
          fontSize: '0.9rem'
        }}>
          {invoice?.dostawca_nazwa || 'Nieznany dostawca'}
        </span>
        <button
          onClick={() => setShowSupplierSearch(true)}
          style={{
            padding: '4px 10px', fontSize: '11px', backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--text-secondary)'
          }}
          title="Zmien dostawce"
        >
          <FaSearch size={9} /> Zmien
        </button>
      </div>
    );
  }

  return (
    <div ref={supplierSearchRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Szukaj dostawcy (nazwa, NIP, telefon)..."
          value={supplierSearchQuery}
          onChange={(e) => handleSupplierSearch(e.target.value)}
          autoFocus
          style={{
            flex: 1, padding: '6px 10px', fontSize: '12px',
            border: '1px solid var(--info)', borderRadius: '6px', outline: 'none',
            backgroundColor: 'var(--card-bg)', color: 'var(--text)'
          }}
        />
        <button
          onClick={() => setShowAddSupplierModal(true)}
          style={{
            padding: '4px 10px', fontSize: '11px', border: '1px solid var(--success)', color: 'var(--success)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: 'var(--card-bg)'
          }}
          title="Dodaj nowego dostawce"
        >
          <FaPlus size={9} /> Nowy
        </button>
        <button
          onClick={() => setShowSupplierSearch(false)}
          style={{
            padding: '4px 10px', fontSize: '11px', border: '1px solid var(--text-muted)', color: 'var(--text-muted)', cursor: 'pointer',
            backgroundColor: 'var(--card-bg)'
          }}
          title="Anuluj"
        >
          <FaTimes size={10} />
        </button>
      </div>

      {(supplierSearchResults.length > 0 || supplierSearchLoading) && (
        <div
          ref={supplierDropdownRef}
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            marginTop: '4px', backgroundColor: 'var(--card-bg)',
            border: '1px solid var(--border)', borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 1000, maxHeight: '200px', overflowY: 'auto'
          }}
        >
          {supplierSearchLoading ? (
            <div style={{ padding: '12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
              Wyszukiwanie...
            </div>
          ) : (
            supplierSearchResults.map((supplier) => (
              <div
                key={supplier.id}
                onClick={() => selectSupplier(supplier)}
                style={{
                  padding: '8px 12px', cursor: 'pointer',
                  borderBottom: '1px solid var(--border)', fontSize: '12px',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--bg)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--card-bg)'}
              >
                <div style={{ fontWeight: '500', color: 'var(--text)' }}>
                  {supplier.typ_klienta === 'firma'
                    ? (supplier.nazwa_firmy || supplier.name)
                    : (supplier.name || `${supplier.imie || ''} ${supplier.nazwisko || ''}`.trim())}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {supplier.nip && <span>NIP: {supplier.nip} | </span>}
                  {(supplier.phone || supplier.telefon) && <span>Tel: {supplier.phone || supplier.telefon}</span>}
                  {supplier.email && !(supplier.phone || supplier.telefon) && <span>{supplier.email}</span>}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default InvoiceInfoPanel;
